import { reactMount } from '@/lib/reactMount'
import { useRef, useEffect, useState } from 'react'
import { Button } from '@/components/Button'
import { getImageByBreakpoint } from '@/lib/media'
import { ArrowRight } from '@/components/Icons'
import { getHeadingClasses } from '@/lib/headings'

// Match heroCarouselBlock: treat viewports < 768px as mobile (no background video)
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : true
  )

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIsMobile()

    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  return isMobile
}

export const pageHeaderBlock = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current
  const theme = umbraco.Theme || 'dark'
  const backgroundColor = umbraco.BlockBackground ? `#${umbraco.BlockBackground}` : '#f8fafc'
  const media = umbraco.Media

  const hasVideo = !!media?.VideoUrl
  const hasImage = !!media?.Image

  const image = media?.Image

  const imageByBreakpoint = hasImage ? getImageByBreakpoint(image) : {}
  const isMobile = useIsMobile()

  // Default to <h1> (page header is typically the page's main heading).
  // Downgrade to <h2> only when the server explicitly marks this block as not-first on the page.
  const isFirstBlockRaw = dataset?.isFirstBlock
  const Heading = isFirstBlockRaw === undefined
    ? 'h1'
    : (isFirstBlockRaw === 'true' ? 'h1' : 'h2')

  // Button logic matching standaloneCtaBlock pattern
  const primary = umbraco.Button
  const secondary = umbraco.SecondaryButton
  const tertiary = umbraco.TertiaryButton
  const hyperlink = umbraco.ButtonText

  const buttons = []

  // Primary
  if (primary) {
    buttons.push({
      ...primary,
      variant: 'primary',
      size: 'lg'
    })
  }

  // Secondary preferred over tertiary
  if (secondary) {
    buttons.push({
      ...secondary,
      variant: 'secondary',
      size: 'lg'
    })
  } else if (tertiary) {
    buttons.push({
      ...tertiary,
      variant: 'primary-alt',
      size: 'lg'
    })
  }
  // Hyperlink only if nothing else exists
  else if (hyperlink) {
    buttons.push({
      ...hyperlink,
      variant: 'link',
      size: 'lg'
    })
  }
  const headingClasses = getHeadingClasses(umbraco.TitleHeadingSize, { fallback: 'medium' })

  useEffect(() => {
    // No visual to wait for, or mobile with video only (no poster/image)
    if (!hasVideo && !hasImage) {
      window.dispatchEvent(new CustomEvent('headerImageLoaded'))
    } else if (isMobile && hasVideo && !hasImage) {
      window.dispatchEvent(new CustomEvent('headerImageLoaded'))
    }
  }, [hasVideo, hasImage, isMobile])

  const handleImageLoad = () => {
    // Dispatch custom event to signal that header image has loaded
    window.dispatchEvent(new CustomEvent('headerImageLoaded'))
  }

  const backdropPicture = hasImage
    ? (
      <picture>
        {Object.keys(imageByBreakpoint).reverse().map((minWidth) => (
          <source
            key={minWidth}
            media={`(min-width: ${minWidth})`}
            srcSet={imageByBreakpoint[minWidth]}
          />
        ))}
        <img
          alt={image?.AltText}
          src={image?.Url}
          onLoad={handleImageLoad}
          onError={handleImageLoad}
          className='absolute inset-0 -z-10 size-full object-cover w-full h-full object-center xl:object-right md:object-center'
        />
      </picture>
      )
    : null

  let backgroundMedia = null
  if (isMobile) {
    backgroundMedia = backdropPicture
  } else if (hasVideo) {
    backgroundMedia = (
      <video
        src={media.VideoUrl}
        poster={image?.Url}
        preload='metadata'
        autoPlay
        muted
        loop
        playsInline
        className='absolute inset-0 -z-10 size-full object-cover w-full h-full'
        onLoadedData={() => window.dispatchEvent(new CustomEvent('headerImageLoaded'))}
        onError={() => window.dispatchEvent(new CustomEvent('headerImageLoaded'))}
      />
    )
  } else {
    backgroundMedia = backdropPicture
  }

  return (
    <div
      id={umbraco.AnchorTarget || undefined}
      data-theme={theme}
      style={{ backgroundColor }}
      className='_headerBlock-section relative flex isolate min-h-[470] md:min-h-[484] lg:min-h-[496px] text-[--theme-text]'
    >
      <div className='absolute inset-0 -z-10 transform-gpu overflow-hidden _hero-g-l-r'>
        <div className='absolute inset-0 bg-hero-gradient' />
        {backgroundMedia}
      </div>

      <div className='grow justify-end mx-auto max-w-7xl flex flex-col lg:gap-x-8 pt-24 pb-20'>
        <div className='max-w-2xl lg:max-w-3xl lg:col-span-7 xl:col-span-6 px-4 py-6 sm:px-6 lg:px-8'>
          {
            umbraco.Label &&
              <span className='uppercase text-[10px] font-extrabold'>{umbraco.Label}</span>
          }
          {
            umbraco.Title &&
              <Heading className={`text-balance ${headingClasses} font-extrabold tracking-tight text-[--theme-text] mb-4`}>
                {umbraco.Title}
              </Heading>
          }
          {
            umbraco.Summary &&
              <div
                className='max-w-2xl mb-6 last:mb-0 text-pretty text-base font-semibold text-[--theme-text]'
                dangerouslySetInnerHTML={{ __html: umbraco.Summary }}
              />
          }
          {buttons.length > 0 &&
            <div className='mt-8 flex items-center flex-wrap justify-start gap-y-2.5 gap-x-6'>
              {buttons.map((btn, index) => (
                <Button
                  key={index}
                  href={btn.Url}
                  target={btn.Target}
                  variant={btn.variant}
                  size={btn.size}
                  componentName={btn.Url?.includes('#apply-dialog') ? 'Header Block' : null}
                >
                  {btn.variant === 'primary-alt'
                    ? (
                      <span className='block relative pr-7'>{btn.Name} <ArrowRight className='absolute -right-0.5 top-1/2 -mt-[11px]' /></span>
                      )
                    : (
                        btn.Name
                      )}
                </Button>
              ))}
            </div>}
        </div>
      </div>
    </div>
  )
}

reactMount('pageHeaderBlock', pageHeaderBlock)
